/**
 * Tokens — the ONLY coupling between surfaces and infrastructure. Defined once
 * here, imported everywhere else (tokens are identity-based). Plus the SPA's
 * mutating-call transport: every mutating /api call carries the CSRF header.
 */

import { createToken, type CommandTransport, type PushChannel, type Router } from "@platform/web-kit"
import type { ApplicationStore, AssetStore, BreadcrumbBus, EvidenceStore, GenerationRunStore, IngestStore, OpportunityStore, OrgStore, SearchRunStore, SectionNavBus, ShellSession, WorkshopStore } from "./domain"
import type { CareerI18n } from "../i18n"

export const SearchRunStoreToken = createToken<SearchRunStore>("career-search-run-store")
export const IngestStoreToken = createToken<IngestStore>("career-ingest-store")
export const EvidenceStoreToken = createToken<EvidenceStore>("career-evidence-store")
export const AssetStoreToken = createToken<AssetStore>("career-asset-store")
export const WorkshopStoreToken = createToken<WorkshopStore>("career-workshop-store")
export const OpportunityStoreToken = createToken<OpportunityStore>("career-opportunity-store")
export const OrgStoreToken = createToken<OrgStore>("career-org-store")
export const ApplicationStoreToken = createToken<ApplicationStore>("career-application-store")
export const GenerationRunStoreToken = createToken<GenerationRunStore>("career-generation-run-store")
export const RouterToken = createToken<Router>("career-router")
export const I18nToken = createToken<CareerI18n>("career-i18n")
export const SessionToken = createToken<ShellSession>("career-session")
export const PushToken = createToken<PushChannel>("career-push")
export const BreadcrumbToken = createToken<BreadcrumbBus>("career-breadcrumb")
export const SectionNavToken = createToken<SectionNavBus>("career-section-nav")

/** CSRF: every mutating /api call carries this header (BFF rejects it if absent). */
export const CSRF_HEADER = "X-Career-Csrf"

/**
 * The dead-session reaction, overridable in tests. Default: navigate to the BFF
 * login (a full page-load that clears the stale logged-in chrome). SSO silently
 * re-completes if the Keycloak session is still alive, else the login form shows.
 * A `returnTo` brings the user back to where they were.
 */
export const sessionExpiryHandler: { redirect: () => void } = {
  redirect() {
    if (typeof location === "undefined") return
    const returnTo = location.pathname + location.search
    location.assign(`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`)
  },
}

/** Once we begin redirecting, ignore further dead-session signals (no redirect loop). */
let redirectingToLogin = false

// ─── Insufficient-credits (402) — one shared reaction, never per-surface ────────
//
// A metered action the caller can't afford returns HTTP 402 `PAYMENT_REQUIRED`. Rather
// than teach every surface to recognise it, the SHARED fetch layer raises a global
// signal (a window CustomEvent) that the shell turns into ONE inline notice with a link
// to /credits — the same "handle-once-globally" shape as {@link reactToSessionExpiry}.

/** The window event the shell listens for to show the "top up your balance" notice. */
export const PAYMENT_REQUIRED_EVENT = "career:payment-required"
/** The window event the credits surface fires once a purchase has landed, so the shell chip refreshes. */
export const CREDITS_UPDATED_EVENT = "career:credits-updated"

/**
 * What the shared notice carries. Two shapes of the SAME product state (you need credits):
 * - `insufficient` (HTTP 402) — you have SOME balance but not enough for THIS action; the api
 *   sends `{ action, required, balance }`.
 * - `blocked` (HTTP 403, the entitlement gate) — you're not entitled at all (zero balance, no
 *   active subscription), so nothing metered even reaches the 402 floor. This is the literal
 *   first-run case: `gateMiddleware` denies before `meteredGate`.
 */
export interface PaymentRequiredDetail {
  kind?: "insufficient" | "blocked"
  action?: string
  required?: number
  balance?: number
}

/** Thrown by the shared client on a 402/entitlement-403 so a caller CAN branch on it; the global notice is already raised. */
export class PaymentRequiredError extends Error {
  constructor(readonly detail: PaymentRequiredDetail) {
    super("payment required")
    this.name = "PaymentRequiredError"
  }
}

/** True when the call was blocked for lack of credits (402 insufficient OR the entitlement 403). */
export function isPaymentRequired(e: unknown): e is PaymentRequiredError {
  return e instanceof PaymentRequiredError
}

/** The api's entitlement-gate 403 body (must be distinguished from cross-user / other 403s). */
const isNotEntitledBody = (body: { code?: string; message?: string } | null): boolean =>
  body?.code === "FORBIDDEN" && typeof body.message === "string" && /not entitled/i.test(body.message)

/**
 * A metered `/api/*` call that fails for lack of credits → raise the shared "top up" signal so
 * the shell shows ONE inline notice (never crediting client-side; the user tops up on /credits).
 * Covers BOTH the 402 insufficient floor AND the entitlement-gate 403 (zero-balance first run).
 * Other 403s (cross-user isolation, etc.) are NOT swallowed — they fall through to a normal error.
 * Returns the detail so the caller can throw a {@link PaymentRequiredError} carrying it.
 */
async function reactToEntitlementBlock(res: Response, path: string): Promise<PaymentRequiredDetail | null> {
  if (path.startsWith("/api/auth/") || (res.status !== 402 && res.status !== 403)) return null
  const body = (await res.clone().json().catch(() => null)) as { code?: string; message?: string; data?: PaymentRequiredDetail } | null
  let detail: PaymentRequiredDetail | null = null
  if (res.status === 402) detail = { kind: "insufficient", ...(body?.data ?? {}) }
  else if (isNotEntitledBody(body)) detail = { kind: "blocked", balance: 0 }
  if (!detail) return null
  if (typeof globalThis.dispatchEvent === "function") {
    globalThis.dispatchEvent(new CustomEvent<PaymentRequiredDetail>(PAYMENT_REQUIRED_EVENT, { detail }))
  }
  return detail
}

/**
 * When the BFF reports an EXPIRED session (`401 { error: "session_expired" }` on a
 * data-plane `/api/*` call), the shell's logged-in state is stale → log OUT and
 * redirect to login rather than surfacing a raw 401 under authenticated chrome
 * (FIX #28 / operator-flagged "Angemeldet als … over HTTP 401"). We NEVER redirect
 * for `/api/auth/*` calls themselves (would loop the login round-trip). Returns
 * true when a redirect was triggered (the caller still throws so no data is used).
 */
async function reactToSessionExpiry(res: Response, path: string): Promise<void> {
  if (res.status !== 401 || redirectingToLogin || path.startsWith("/api/auth/")) return
  const signal = (await res.clone().json().catch(() => null)) as { error?: string } | null
  if (signal?.error !== "session_expired") return
  redirectingToLogin = true
  sessionExpiryHandler.redirect()
}

/** Raise the shared credits notice (402 or entitlement-403), then throw a typed error; else a generic one. */
async function throwHttpError(res: Response, verb: string, path: string): Promise<never> {
  await reactToSessionExpiry(res, path)
  const block = await reactToEntitlementBlock(res, path)
  if (block) throw new PaymentRequiredError(block)
  throw new Error(`${verb} ${path} failed: HTTP ${res.status}`)
}

/**
 * The metered `/api` paths (procedures behind `meteredGate` — every one debits the wallet). A
 * successful call to any of these changes the balance, so the shared layer fires `CREDITS_UPDATED`
 * once here (NOT per surface) → the shell chip re-fetches. Detached runs (search/generate) accrue
 * cost as they progress; the immediate refresh catches the synchronous debit, and each further
 * metered action (or a navigation) reconciles the rest.
 */
function isMeteredPath(path: string): boolean {
  const p = path.split("?")[0] ?? path
  if (p === "/api/search/start" || p === "/api/applications/generate" || p === "/api/profile/narrative") return true
  return p.startsWith("/api/workshops/") && (p.endsWith("/turn") || p.endsWith("/stimulus") || p.endsWith("/consolidate"))
}

/** POST JSON to a BFF `/api` route with the CSRF header attached. */
export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", [CSRF_HEADER]: "1" },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) await throwHttpError(res, "POST", path)
  const data = (await res.json()) as T
  // A metered action just debited the wallet → nudge the balance chip to re-fetch (shared, once).
  if (isMeteredPath(path) && typeof globalThis.dispatchEvent === "function") {
    globalThis.dispatchEvent(new CustomEvent(CREDITS_UPDATED_EVENT))
  }
  return data
}

/** GET JSON from a BFF `/api` route. */
export async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(path, { headers: { Accept: "application/json" } })
  if (!res.ok) await throwHttpError(res, "GET", path)
  return (await res.json()) as T
}

/** Upload a multipart form to a BFF `/api` route (browser sets the boundary). */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(path, { method: "POST", headers: { [CSRF_HEADER]: "1" }, body: form })
  if (!res.ok) await throwHttpError(res, "UPLOAD", path)
  return (await res.json()) as T
}

// ─── Billing (Step 5) — the typed wallet + checkout client (contract §3) ────────

/** The credit wallet as the api returns it (`GET /billing/wallet`). */
export interface WalletView {
  balance: number
  granted: number
  consumed: number
}

/** A parse error means the api answered but not with a wallet — surface it as "unavailable". */
const numOr = (v: unknown, fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? v : fallback)

/** Fetch the caller's credit wallet. Auth-only (never gated / never 402). */
export async function fetchWallet(): Promise<WalletView> {
  const raw = await apiGet<Record<string, unknown>>("/api/billing/wallet")
  return { balance: numOr(raw["balance"], 0), granted: numOr(raw["granted"], 0), consumed: numOr(raw["consumed"], 0) }
}

/** Raised when Stripe isn't configured server-side (503) — the surface shows a calm state. */
export class StripeNotConfiguredError extends Error {
  constructor() {
    super("stripe not configured")
    this.name = "StripeNotConfiguredError"
  }
}

/**
 * Start a hosted Stripe checkout for a credit pack; returns the URL to redirect to. The
 * hosted page is the ONLY payment surface (zero PCI — no Stripe.js, no card fields here).
 * A 503 (`STRIPE_NOT_CONFIGURED`) becomes a typed error so the surface can explain calmly.
 */
export async function startCheckout(packId: string): Promise<{ url: string }> {
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json", [CSRF_HEADER]: "1" },
    body: JSON.stringify({ packId }),
  })
  if (res.status === 503) throw new StripeNotConfiguredError()
  if (!res.ok) await throwHttpError(res, "POST", "/api/billing/checkout")
  const raw = (await res.json()) as { url?: unknown }
  if (typeof raw.url !== "string" || raw.url.length === 0) throw new Error("checkout returned no url")
  return { url: raw.url }
}

/**
 * The evidence store's command transport — maps web-kit optimistic commands onto
 * the api's oRPC verbs. The authoritative reconciling event arrives over the PUSH
 * plane (the review surface subscribes the Evidence stream), so success returns no
 * envelopes; the optimistic patch (ABSOLUTE end-state) stands until push folds it.
 */
export function makeEvidenceCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    "confirm-claim": "/api/evidence/confirm",
    "discard-claim": "/api/evidence/discard",
    "edit-claim": "/api/evidence/edit",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The asset store's command transport — optimistic confirm/remove predict the
 * ABSOLUTE end-state (status confirmed / removed); the api call persists it and the
 * surface re-fetches `assets.list` to reconcile from server truth. Success returns
 * no envelopes, so the optimistic patch stands until the refresh folds authority.
 */
export function makeAssetCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    "confirm-asset": "/api/assets/confirm",
    "remove-asset": "/api/assets/remove",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The opportunity store's command transport — optimistic bookmark/dismiss/delete
 * predict the ABSOLUTE end-state (bookmarked / dismissed / removed); the api call
 * persists it and the search surface re-fetches `opportunities.list` to reconcile
 * from server truth. Success returns no envelopes, so the optimistic patch stands
 * until the refresh folds authority.
 */
export function makeOpportunityCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    bookmark: "/api/opportunities/bookmark",
    dismiss: "/api/opportunities/dismiss",
    delete: "/api/opportunities/delete",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}

/**
 * The application command transport — optimistic lifecycle + edit-loop commands. Each predicts the
 * ABSOLUTE end-state (status / edit resolution); the api call persists it and the review surface
 * re-fetches to reconcile from server truth.
 */
export function makeApplicationCommandTransport(): CommandTransport {
  const ROUTES: Record<string, string> = {
    transition: "/api/applications/transition",
    "edit-approve": "/api/applications/edit/approve",
    "edit-reject": "/api/applications/edit/reject",
  }
  return async (cmd) => {
    const path = ROUTES[cmd.type]
    if (!path) return { ok: false, error: `unknown command ${cmd.type}` }
    try {
      await apiPost(path, cmd.payload)
      return { ok: true, envelopes: [] }
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : String(e) }
    }
  }
}
